<template>
  <b-overlay :show="loading" opacity="0.8">
    <validation-observer ref="form_ref" #default="{ invalid }">
      <b-card class="rounded-lg">
        <template #header>
          <div class="d-flex justify-content-between align-items-center w-100 pb-1" style="border-bottom: 2px solid rgb(238, 238, 238);">
            <h4 class="text-dark m-0">
              Vitals
            </h4>

            <b-button variant="outline-primary" :disabled="invalid" @click="onUpdateVitals">
              Update
            </b-button>
          </div>
        </template>

          <div class="card-body">
            <div class="d-flex align-items-start justify-content-between">
              <div class="w-50 d-flex align-items-center flex-column pr-1">
                <div>
                  <b-form-group label="Blood Pressure" label-cols-sm="7" label-cols-lg="7" content-col-sm content-cols-lg-4>
                    <validation-provider #default="{ errors }" name="Blood Pressure" rules="required">
                      <div class="d-flex">
                        <div>
                          <b-form-input id="systolic" v-model="form_data.vitals.blood_pressure.systolic" name="systolic" />
                          <sub>Systolic</sub>
                        </div>
                        
                        <div class="">
                          <b-form-input id="diastolic" v-model="form_data.vitals.blood_pressure.diastolic" name="diastolic" />
                          <sub>Diastolic</sub>
                        </div>
                      </div>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group label="Heart Rate (BPM)" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                    <validation-provider #default="{ errors }" name="Heart Rate (BPM)" rules="required">
                      <b-form-input id="heart_rate" v-model="form_data.vitals.heart_rate" name="heart_rate" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group label="Respiratory Rate" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                    <validation-provider #default="{ errors }" name="Respiratory Rate" rules="required">
                      <b-form-input id="respiratory_rate" v-model="form_data.vitals.respiratory_rate" name="respiratory_rate" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group label="Body Temperature" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                    <template #label>
                      <span>Body Temperature<sub>(°C)</sub></span>
                    </template>
                    <validation-provider #default="{ errors }" name="Body Temperature" rules="required">
                      <b-form-input id="body_temperature" v-model="form_data.vitals.body_temperature" name="body_temperature" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group label="SPO2 (Oxygen Saturation)" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                    <template #label>
                      <span>SPO2<sub>(Oxygen Saturation)</sub></span>
                    </template>

                    <validation-provider #default="{ errors }" name="SPO2 (Oxygen Saturation)" rules="required">
                      <b-form-input id="oxygen_saturation" v-model="form_data.vitals.oxygen_saturation" name="oxygen_saturation" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group label="FBS - mmol/L" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                    <template #label>
                      <span>FBS<sub>(mmol/L)</sub></span>
                    </template>

                    <validation-provider #default="{ errors }" name="FBS - mmol/L" rules="required">
                      <b-form-input id="fasting_blood_sugar" v-model="form_data.vitals.fasting_blood_sugar" name="fasting_blood_sugar" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group label="RBS - mmol/L" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                    <template #label>
                      <span>RBS<sub>(mmol/L)</sub></span>
                    </template>

                    <validation-provider #default="{ errors }" name="RBS - mmol/L" rules="required">
                      <b-form-input id="random_blood_sugar" v-model="form_data.vitals.random_blood_sugar" name="random_blood_sugar" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                </div>
              </div>
        
              <div class="w-50 pl-1" style="border-left: 2px dotted rgb(238, 238, 238);">
                <b-form-group label="Weight" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                  <template #label>
                    <span>Weight<sub>(kg)</sub></span>
                  </template>

                  <validation-provider #default="{ errors }" name="Weight" rules="required">
                    <b-form-input id="weight" v-model="form_data.anthropometric_data.weight" name="weight" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group label="Height" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                  <template #label>
                    <span>Height<sub>(cm)</sub></span>
                  </template>

                  <validation-provider #default="{ errors }" name="Height" rules="required">
                    <b-form-input id="height" v-model="form_data.anthropometric_data.height" name="height" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group label="Abdominal Circumference" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                  <template #label>
                    <span>Abdominal Circumference<sub>(cm)</sub></span>
                  </template>

                  <validation-provider #default="{ errors }" name="Abdominal Circumference" rules="required">
                    <b-form-input id="abdominal_circumference" v-model="form_data.anthropometric_data.abdominal_circumference" name="abdominal_circumference" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group label="Waist Circumference" label-cols-sm="4" label-cols-lg="7" content-col-sm content-cols-lg-4>
                  <template #label>
                    <span>Waist Circumference<sub>(cm)</sub></span>
                  </template>

                  <validation-provider #default="{ errors }" name="Waist Circumference" rules="required">
                    <b-form-input id="waist_circumference" v-model="form_data.anthropometric_data.waist_circumference" name="waist_circumference" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </div>
            </div>
          </div>
      </b-card>
    </validation-observer>
  </b-overlay>
</template>

<script>
import {
  BRow,
  BCol,
  BImg,
  BCard,
  BBadge,
  BAlert,
  BButton,
  BAvatar,
  BOverlay,
  BDropdown,
  BFormGroup,
  BFormInput,
  BDropdownItem,
  BDropdownGroup
} from 'bootstrap-vue'

import { get } from 'lodash';
import { heightTransition } from '@core/mixins/ui/transition';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  components: {
    BRow,
    BCol,
    BImg,
    BCard,
    BBadge,
    BAlert,
    BAvatar,
    BButton,
    BOverlay,
    BDropdown,
    BFormGroup,
    BFormInput,
    BDropdownItem,
    BDropdownGroup,

    // validations
    ValidationProvider,
    ValidationObserver
  },
  mixins: [heightTransition],
  props: {
    consultationData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      form_data: this.consultationData
    }
  },
  methods: {
    async onUpdateVitals(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.form_ref.validate();
        if (!success){
          return;
        }

        if (!this.can(`consultations:CanUpdate`)) {
          return
        }
        
        const { id, vitals, anthropometric_data } = this.form_data
        const response = await this.useJwt().mainService.consultations.update(id, {
          vitals,
          anthropometric_data
        });

        this.$emit('update', response.data.data);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Vitals updated successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
  }
}
</script>

<style scoped>
.profile-highlight {
  font-size: 18px !important;
}
.profile-highlight-title {
  font-size: 16px !important;
  color: lightgray;
  font-weight: normal;
}

.patient-name {
  font-size: 25px;
}
</style>
